// src/pages/shift_report.js
import React, { useEffect, useState, useContext } from "react";
import Header from "../components/header";
import { LanguageContext } from "../context/LanguageContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getShifts, getMachines, getShiftReport } from "../api";

// ✅ Recharts (estética igual a tu otro informe)
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import renderActiveShape from "../components/renderActiveShape";

function ShiftReport() {
  const { t } = useContext(LanguageContext);

  // Empresa desde localStorage
  const company = JSON.parse(localStorage.getItem("company"));
  const id_c = company?.id_c;

  // Selectores
  const [dateSel, setDateSel] = useState(null); // Date
  const [codMaq, setCodMaq] = useState("");
  const [idShift, setIdShift] = useState("");

  // Catálogos
  const [shifts, setShifts] = useState([]);
  const [machines, setMachines] = useState([]);

  // Datos del reporte
  const [shiftInfo, setShiftInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    sumHours: 0,
    sumPercent: 0,
    conflicts: 0,
    batchCount: 0,
  });

  // Tabla de agrupación (D)
  const [groupRows, setGroupRows] = useState([]);

  // Estados para resaltar slice activo (Recharts)
  const [activeIndexPie1, setActiveIndexPie1] = useState(0);
  const [activeIndexPie2, setActiveIndexPie2] = useState(0);

  const onPieEnter1 = (_, index) => setActiveIndexPie1(index);
  const onPieEnter2 = (_, index) => setActiveIndexPie2(index);

  // Cargar combos (turnos y máquinas)
  useEffect(() => {
    if (!id_c) return;
    (async () => {
      try {
        const shiftsResp = await getShifts(id_c);       // { shifts, total }
        const machinesResp = await getMachines(id_c);   // { machines, ... }
        setShifts(shiftsResp?.shifts || []);
        setMachines(machinesResp?.machines || []);
      } catch (e) {
        console.error("❌ Error cargando catálogos:", e);
      }
    })();
  }, [id_c]);

  // Helpers
  const fmt2 = (n) => (Number.isFinite(n) ? Number(n).toFixed(2) : "0.00");

  const computeConflicts = (ordered) => {
    let conflicts = 0;
    const result = ordered.map((r, idx) => {
      if (idx === 0) return { ...r, time_conflict: "NO" };
      const prevEnd = ordered[idx - 1].end_ts;
      const curStart = r.start_ts;
      const conflict = curStart < prevEnd;
      if (conflict) conflicts += 1;
      return { ...r, time_conflict: conflict ? "SI" : "NO" };
    });
    return { result, conflicts };
  };

  const buildGrouping = (ordered, totalShiftHours) => {
    // Agrupa por name_activity
    const map = new Map();
    let sumUsed = 0;

    ordered.forEach((r) => {
      sumUsed += r.hours_used || 0;
      const key = r.name_activity || "N/A";
      map.set(key, (map.get(key) || 0) + (r.hours_used || 0));
    });

    const arr = Array.from(map.entries()).map(([name, hrs]) => ({
      name_activity: name,
      total_hours: Number(hrs.toFixed(2)),
      percent_of_shift:
        totalShiftHours > 0
          ? Number(((hrs / totalShiftHours) * 100).toFixed(2))
          : 0,
    }));

    // "No programado"
    const missing = Math.max(
      0,
      Number(((totalShiftHours || 0) - sumUsed).toFixed(2))
    );
    arr.push({
      name_activity: "No programado",
      total_hours: missing,
      percent_of_shift:
        totalShiftHours > 0
          ? Number(((missing / totalShiftHours) * 100).toFixed(2))
          : 0,
    });

    // Fila total (para validar)
    const totalHours = Number(
      arr.reduce((acc, r) => acc + r.total_hours, 0).toFixed(2)
    );
    const totalPercent = Number(
      arr.reduce((acc, r) => acc + r.percent_of_shift, 0).toFixed(2)
    );
    arr.push({
      name_activity: "TOTAL",
      total_hours: totalHours,
      percent_of_shift: totalPercent,
    });

    return arr;
  };

  const handleConsultar = async () => {
    try {
      if (!id_c || !dateSel || !codMaq || !idShift) {
        alert("Por favor selecciona Fecha, Turno y Máquina.");
        return;
      }

      const dateStr = dateSel.toISOString().slice(0, 10);

      const data = await getShiftReport(id_c, {
        date: dateStr,
        cod_maq: codMaq,
        id_shift: idShift,
      });

      // Resumen del turno (B)
      setShiftInfo(data.shift || null);

      // Unificar items (C)
      const items = (data.items || []).map((it) => ({ ...it }));

      // Orden ascendente por inicio
      items.sort((a, b) =>
        a.start_ts < b.start_ts ? -1 : a.start_ts > b.start_ts ? 1 : 0
      );

      // Totales base
      const sumHours = items.reduce((acc, r) => acc + (r.hours_used || 0), 0);
      const withPercent = items.map((r) => ({
        ...r,
        percent_row:
          sumHours > 0
            ? Number(((r.hours_used / sumHours) * 100).toFixed(2))
            : 0,
      }));

      // Conflictos
      const { result: withConflicts, conflicts } = computeConflicts(withPercent);

      // Conteos de totales de la fila final
      const batchCount = withConflicts.filter(
        (r) => r.id_batch !== null && r.id_batch !== undefined
      ).length;
      const sumPercent = Number(
        withConflicts.reduce((acc, r) => acc + r.percent_row, 0).toFixed(2)
      );

      setRows(withConflicts);
      setTotals({
        sumHours: Number(sumHours.toFixed(2)),
        sumPercent,
        conflicts,
        batchCount,
      });

      // Tabla de agrupación (D): porcentajes respecto al total del turno
      const totalShiftHours = Number(data.shift?.total_hr_shift || 0);
      setGroupRows(buildGrouping(withConflicts, totalShiftHours));

      // Reset de highlight de pies
      setActiveIndexPie1(0);
      setActiveIndexPie2(0);
    } catch (err) {
      console.error("❌ Error consultando shift report:", err);
      alert("Error al consultar el informe de turno.");
    }
  };

  // ===================== DATA PARA PIE CHARTS =====================

  /* ______________________________________________________________
    //v1
    // Pie #1: Operación (type_boolean=1) vs Otras actividades (0) — respecto a horas usadas (tabla 1)
    const opHoursFromRows = rows.reduce(
      (acc, r) => acc + (r.type_boolean ? (r.hours_used || 0) : 0),
      0
    );
    const usedHours = totals.sumHours || 0;
    const otherHours = Math.max(0, usedHours - opHoursFromRows);
  
    const pie1Data =
      usedHours > 0
        ? [
            { name: "Operación", value: Number(opHoursFromRows.toFixed(2)) },
            { name: "Otras actividades", value: Number(otherHours.toFixed(2)) },
          ]
        : [];
  
  */

  //v2
  // Pie #1 (AJUSTADO):
  // Operación (type_boolean=1) vs No operación (incluye NO PROGRAMADO)
  // El total del gráfico es TODO el turno (total_hr_shift), no solo lo usado.
  const totalShiftHours = Number(shiftInfo?.total_hr_shift || 0);

  // Horas de operación realmente registradas
  const rawOp = rows.reduce(
    (acc, r) => acc + (r.type_boolean ? (r.hours_used || 0) : 0),
    0
  );

  // Acotar por seguridad ante inconsistencias (p. ej. solapes)
  const opHoursFromRows = Math.min(Number(rawOp.toFixed(2)), totalShiftHours);

  // "No operación" incluye:
  // - Otras actividades registradas (no-op)
  // - + el faltante no programado del turno
  const otherHours = Math.max(0, Number((totalShiftHours - opHoursFromRows).toFixed(2)));

  const pie1Data =
    totalShiftHours > 0
      ? [
        { name: "Operación", value: opHoursFromRows },
        { name: "No operación (incluye no programado)", value: otherHours },
      ]
      : [];



  // Paleta (compatibles con tu ejemplo)
  const PIE1_COLORS = ["#109443", "#5972a0"];

  // Pie #2: Distribución del turno por actividad (tabla 2, incluye “No programado”)
  // Usamos horas absolutas; el total de slices = total horas de turno
  const groupNoTotal = groupRows.filter((g) => g.name_activity !== "TOTAL");
  const pie2Data =
    (shiftInfo && groupNoTotal?.length > 0)
      ? groupNoTotal.map((g) => ({
        name: g.name_activity,
        value: Number(g.total_hours || 0),
      }))
      : [];

  // Paleta extensa
  const PIE2_COLORS = [
    "#133247", "#109443", "#454958", "#ce649e",
    "#2d5838", "#5972a0", "#7f55d0", "#e69e33",
    "#1c8aa6", "#b84d4d"
  ];

  return (
    <div className="body">
      <Header />

      <h2>Reporte de Turno</h2>

      {/* Filtros */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(200px, 1fr))",
          gap: "10px",
          alignItems: "end",
          marginBottom: "10px",
        }}
      >
        <div>
          <label className="text2">Fecha del turno</label>
          <DatePicker
            className="dateSpace"
            selected={dateSel}
            onChange={(d) => setDateSel(d)}
            dateFormat="dd/MM/yyyy"
            placeholderText="Selecciona fecha"
            isClearable
          />
        </div>

        <div>
          <label className="text2">Turno</label>
          <select
            className="input1"
            value={idShift}
            onChange={(e) => setIdShift(e.target.value)}
          >
            <option value="">-- Selecciona --</option>
            {shifts.map((s) => (
              <option key={s.id_shift} value={s.id_shift}>
                {s.id_shift} (Inicio: {String(s.time_begin).slice(0, 5)} - Fin:{" "}
                {String(s.time_end).slice(0, 5)})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text2">Máquina</label>
          <select
            className="input1"
            value={codMaq}
            onChange={(e) => setCodMaq(e.target.value)}
          >
            <option value="">-- Selecciona --</option>
            {machines.map((m) => (
              <option key={m.cod_maq} value={m.cod_maq}>
                {m.cod_maq} {m.name_m ? `- ${m.name_m}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button className="button4" onClick={handleConsultar}>
            Consultar
          </button>
        </div>
      </div>

      <hr />

      {/* Resumen del turno (B) */}
      {shiftInfo && (
        <div style={{ marginBottom: "12px" }}>
          <h3>Resumen del turno</h3>
          <table className="table1">
            <thead>
              <tr>
                <th>Fecha inicio turno</th>
                <th>Hora inicio</th>
                <th>Fecha fin turno</th>
                <th>Hora fin</th>
                <th>Total horas turno</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{shiftInfo.shift_start.slice(0, 10)}</td>
                <td>{String(shiftInfo.time_begin).slice(0, 5)}</td>
                <td>{shiftInfo.shift_end.slice(0, 10)}</td>
                <td>{String(shiftInfo.time_end).slice(0, 5)}</td>
                <td>{fmt2(shiftInfo.total_hr_shift)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Tabla principal (C) */}
      {rows.length > 0 && (
        <>
          <h3>Detalle de actividades y lotes del turno</h3>
          <table className="table1">
            <thead>
              <tr>
                <th>1- Fecha inicio</th>
                <th>2- Hr inicio</th>
                <th>3- Hr fin</th>
                <th>4- Total horas usadas</th>
                <th>5- Nombre de actividad</th>
                <th>6- Tipo de actividad (bool)</th>
                <th>7- % tiempo del turno</th>
                <th>8- Time Conflict</th>
                <th>9- ID Lote</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.start_ts}-${i}`}>
                  <td>{r.start_date}</td>
                  <td>{r.start_time}</td>
                  <td>{r.end_time}</td>
                  <td>{fmt2(r.hours_used)}</td>
                  <td>{r.name_activity}</td>
                  <td style={{ textAlign: "center" }}>{r.type_boolean ? 1 : 0}</td>
                  <td>{fmt2(r.percent_row)}%</td>
                  <td
                    style={{
                      backgroundColor:
                        r.time_conflict === "SI" ? "red" : "transparent",
                      color: r.time_conflict === "SI" ? "white" : "inherit",
                      fontWeight: r.time_conflict === "SI" ? "bold" : "normal",
                      textAlign: "center",
                    }}
                  >
                    {r.time_conflict}
                  </td>
                  <td>{r.id_batch || ""}</td>
                </tr>
              ))}
              {/* Fila de totales */}
              <tr>
                <td colSpan={3} style={{ textAlign: "right", fontWeight: "bold" }}>
                  TOTALES
                </td>
                <td style={{ fontWeight: "bold" }}>{fmt2(totals.sumHours)}</td>
                <td></td>
                <td></td>
                <td style={{ fontWeight: "bold" }}>{fmt2(totals.sumPercent)}%</td>
                <td style={{ fontWeight: "bold", textAlign: "center" }}>
                  {totals.conflicts}
                </td>
                <td style={{ fontWeight: "bold", textAlign: "center" }}>
                  {totals.batchCount}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* Tabla de agrupación (D) */}
      {groupRows.length > 0 && (
        <>
          <h3>Totales por actividad (incluye &quot;No programado&quot;)</h3>
          <table className="table1">
            <thead>
              <tr>
                <th>Nombre de actividad</th>
                <th>Total horas</th>
                <th>% del turno</th>
              </tr>
            </thead>
            <tbody>
              {groupRows.map((g, idx) => (
                <tr
                  key={`${g.name_activity}-${idx}`}
                  style={{
                    fontWeight: g.name_activity === "TOTAL" ? "bold" : "normal",
                  }}
                >
                  <td>{g.name_activity}</td>
                  <td
                    style={{
                      backgroundColor:
                        (g.name_activity === "No programado" && Number(g.total_hours) > 0) ||
                          (g.name_activity === "TOTAL" && Number(g.total_hours) > Number(shiftInfo?.total_hr_shift || 0))
                          ? "red"
                          : "transparent",
                      color:
                        (g.name_activity === "No programado" && Number(g.total_hours) > 0) ||
                          (g.name_activity === "TOTAL" && Number(g.total_hours) > Number(shiftInfo?.total_hr_shift || 0))
                          ? "white"
                          : "inherit",
                    }}
                  >
                    {fmt2(g.total_hours)}
                  </td>
                  <td>{fmt2(g.percent_of_shift)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}


      {/* ======================= CHARTS ======================= */}
      {(pie1Data.length > 0 || pie2Data.length > 0) && (
        <>
          <div className="">
            <hr />
            <div className="chartsbox2" style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
              {/* Pie #1: Operación vs Otras actividades */}
              {pie1Data.length > 0 && (
                <div className="chart2" style={{ width: 280, height: 320 }}>
                  <h5>Operación vs Otras actividades</h5>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexPie1}
                        activeShape={renderActiveShape}
                        data={pie1Data}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        onMouseEnter={onPieEnter1}
                      >
                        {pie1Data.map((entry, index) => (
                          <Cell key={`pie1-${index}`} fill={PIE1_COLORS[index % PIE1_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Pie #2: Distribución del turno por actividad */}
              {pie2Data.length > 0 && (
                <div className="chart2" style={{ width: 320, height: 340 }}>
                  <h5>Distribución del turno por actividad</h5>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexPie2}
                        activeShape={renderActiveShape}
                        data={pie2Data}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        onMouseEnter={onPieEnter2}
                      >
                        {pie2Data.map((entry, index) => (
                          <Cell key={`pie2-${index}`} fill={PIE2_COLORS[index % PIE2_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

          </div>
        </>
      )}
    </div>
  );
}

export default ShiftReport;
